import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../firebase/config";
import { doc, getDoc, collection, query, where, getDocs, setDoc, updateDoc } from "firebase/firestore";

const AdminCustomerCalendar = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [deliveries, setDeliveries] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingDate, setEditingDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setLoading(true);
        
        // Fetch customer data
        const customerDoc = await getDoc(doc(db, "users", customerId));
        if (!customerDoc.exists()) {
          alert("Customer not found");
          navigate("/admin/customers");
          return;
        }
        setCustomer({ id: customerDoc.id, ...customerDoc.data() });

        // Fetch delivery data
        const deliveryQuery = query(
          collection(db, "deliveries"),
          where("userId", "==", customerId)
        );
        const deliverySnap = await getDocs(deliveryQuery);
        const deliveryMap = {};
        deliverySnap.forEach(doc => {
          const data = doc.data();
          if (data.date) {
            deliveryMap[data.date] = data.status;
          }
        });
        setDeliveries(deliveryMap);

        // Fetch subscription data
        const subQuery = query(
          collection(db, "subscriptions"),
          where("userId", "==", customerId),
          where("status", "==", "active")
        );
        const subSnap = await getDocs(subQuery);
        if (!subSnap.empty) {
          const subData = { id: subSnap.docs[0].id, ...subSnap.docs[0].data() };
          
          // Calculate original end date (26 delivery days from start)
          const startDate = new Date(subData.serviceStartDate || subData.startDate);
          let currentDate = new Date(startDate);
          let deliveryCount = 0;
          
          // Start from next day after service start
          currentDate.setDate(currentDate.getDate() + 1);
          
          while (deliveryCount < 26) {
            // Skip Mondays
            if (currentDate.getDay() !== 1) {
              deliveryCount++;
            }
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          // Go back one day since we went past the target
          currentDate.setDate(currentDate.getDate() - 1);
          const originalEndDate = currentDate.toISOString().split('T')[0];
          
          // Count missed delivery days from the delivery data
          let missedDays = 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          console.log("🔍 Starting missed days calculation for customer:", customerId);
          console.log("📅 Subscription period:", {
            startDate: subData.serviceStartDate || subData.startDate,
            originalEndDate: originalEndDate,
            today: today.toISOString().split('T')[0]
          });
          
          // Only count "not_delivered" status within the original subscription period
          Object.keys(deliveryMap).forEach(date => {
            const deliveryDate = new Date(date);
            const subStart = new Date(subData.serviceStartDate || subData.startDate);
            const subEnd = new Date(originalEndDate);
            
            // Only count days within the original subscription period AND only "not_delivered" status
            if (deliveryDate >= subStart && deliveryDate <= subEnd) {
              if (deliveryMap[date] === "not_delivered") {
                // Double check it's not a Monday (holiday)
                if (deliveryDate.getDay() !== 1) {
                  missedDays++;
                  console.log("❌ Counted as missed:", date, "status:", deliveryMap[date]);
                } else {
                  console.log("⏭️ Skipped Monday holiday:", date, "status:", deliveryMap[date]);
                }
              } else {
                console.log("⏭️ Skipped (not missed):", date, "status:", deliveryMap[date]);
              }
            } else {
              console.log("⏭️ Skipped (outside subscription period):", date, "status:", deliveryMap[date]);
            }
          });
          
          console.log("🔍 Final missed days breakdown:", {
            totalDeliveryRecords: Object.keys(deliveryMap).length,
            notDeliveredDates: Object.keys(deliveryMap).filter(date => deliveryMap[date] === "not_delivered"),
            calculatedMissedDays: missedDays
          });
          
          // Calculate extended end date by adding missed days
          let extendedEndDate = new Date(originalEndDate);
          let addedDays = 0;
          
          while (addedDays < missedDays) {
            extendedEndDate.setDate(extendedEndDate.getDate() + 1);
            // Skip Mondays when extending
            if (extendedEndDate.getDay() !== 1) {
              addedDays++;
            }
          }
          
          subData.originalEndDate = originalEndDate;
          subData.projectedEndDate = extendedEndDate.toISOString().split('T')[0];
          subData.missedDays = missedDays;
          
          console.log("📊 Missed days calculation:", {
            totalDeliveryRecords: Object.keys(deliveryMap).length,
            notDeliveredCount: Object.values(deliveryMap).filter(status => status === "not_delivered").length,
            pendingCount: Object.values(deliveryMap).filter(status => status === "pending").length,
            calculatedMissedDays: missedDays,
            originalEndDate: originalEndDate,
            extendedEndDate: extendedEndDate.toISOString().split('T')[0]
          });
          
          setSubscription(subData);
          
          // Set current month to subscription start date ONLY on first load
          if (!currentMonth || currentMonth.getTime() === new Date().getTime()) {
            const startDate = new Date(subData.serviceStartDate || subData.startDate);
            setCurrentMonth(startDate);
            console.log("🎯 Setting initial month to:", startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
          }
          
          console.log("📊 Loaded subscription:", {
            startDate: subData.serviceStartDate || subData.startDate,
            originalEndDate: subData.originalEndDate,
            projectedEndDate: subData.projectedEndDate,
            missedDays: subData.missedDays,
            planName: subData.planName
          });
        }

      } catch (error) {
        console.error("Error fetching customer data:", error);
        alert("Error loading customer data");
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchCustomerData();
    }
  }, [customerId, navigate]);

  // Monitor currentMonth changes
  useEffect(() => {
    console.log("🔄 Current month changed to:", currentMonth?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
    if (subscription) {
      console.log("📅 Subscription start date:", subscription.serviceStartDate || subscription.startDate);
      console.log("📅 Subscription end date:", subscription.projectedEndDate || subscription.endDate);
      console.log("🔍 Can go previous:", canGoPrevious());
      console.log("🔍 Can go next:", canGoNext());
    }
  }, [currentMonth, subscription]);

  const getStatusEmoji = (status) => {
    switch (status) {
      case "delivered":
        return "✅";
      case "pending":
        return "⏳";
      case "not_delivered":
        return "❌";
      case "upcoming":
        return "📅";
      case "holiday":
        return "❌";
      default:
        return "•";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "text-green-600";
      case "pending":
        return "text-yellow-600";
      case "not_delivered":
        return "text-red-600";
      case "upcoming":
        return "text-gray-400";
      case "holiday":
        return "text-red-600";
      default:
        return "text-gray-500";
    }
  };

  const generateCalendarDays = useCallback(() => {
    if (!subscription || !currentMonth) return [];

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Get first and last day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get subscription period
    const startDate = new Date(subscription.serviceStartDate || subscription.startDate);
    const endDate = new Date(subscription.projectedEndDate);
    
    console.log("📅 Generating calendar for:", currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
    console.log("📅 Subscription period:", startDate.toLocaleDateString(), "to", endDate.toLocaleDateString());

    const days = [];
    
    // Add empty cells for days before the first day of the month
    const firstDayOfWeek = firstDay.getDay();
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ date: null, status: "empty" });
    }
    
    // Add all days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const dateObj = new Date(year, month, day);
      const key = dateObj.toISOString().split('T')[0];
      const isMonday = dateObj.getDay() === 1;
      
      // Check if this day is within subscription period
      const isInSubscriptionPeriod = dateObj >= startDate && dateObj <= endDate;
      
      // Determine if this day should be shown
      const isRelevantDay = isInSubscriptionPeriod || isMonday;
      
      if (isRelevantDay) {
        let status;
        if (isMonday) {
          status = "monday_holiday"; // Changed from "holiday" to "monday_holiday"
        } else if (isInSubscriptionPeriod) {
          status = deliveries[key] || "upcoming";
        } else {
          status = "empty";
        }
        
        days.push({
          date: dateObj,
          status: status,
          isInSubscriptionPeriod: isInSubscriptionPeriod
        });
      } else {
        days.push({ date: dateObj, status: "empty" });
      }
    }

    return days;
  }, [subscription, currentMonth, deliveries]);

  // Generate calendar days whenever currentMonth changes
  const calendarDays = React.useMemo(() => {
    console.log("🔄 Recalculating calendar for month:", currentMonth?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
    return generateCalendarDays();
  }, [currentMonth, subscription, deliveries, generateCalendarDays]);

  const handleStatusChange = async (date, newStatus) => {
    if (saving) return;
    
    try {
      setSaving(true);
      
      // Update local state immediately for better UX
      setDeliveries(prev => ({
        ...prev,
        [date]: newStatus
      }));

      // Save to Firestore
      const deliveryRef = doc(db, "deliveries", `${customerId}_${date}`);
      await setDoc(deliveryRef, {
        userId: customerId,
        date: date,
        status: newStatus,
        updatedAt: new Date().toISOString(),
        updatedBy: "admin"
      }, { merge: true });

      setEditingDate(null);
      alert("Delivery status updated successfully!");
      
    } catch (error) {
      console.error("Error updating delivery status:", error);
      alert("Error updating delivery status. Please try again.");
      
      // Revert local state on error
      setDeliveries(prev => {
        const newState = { ...prev };
        delete newState[date];
        return newState;
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getMonthsBetween = (startDate, endDate) => {
    const months = [];
    let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    
    while (current <= endDate) {
      months.push({
        year: current.getFullYear(),
        month: current.getMonth(),
      });
      // move to next month
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  };

  const goToPreviousMonth = () => {
    if (!subscription || !currentMonth) return;
    
    const startDate = new Date(subscription.serviceStartDate || subscription.startDate);
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    
    // Check if previous month is within subscription period
    const months = getMonthsBetween(startDate, new Date(subscription.projectedEndDate));
    const prevMonthKey = `${prevMonth.getFullYear()}-${prevMonth.getMonth()}`;
    const isPrevMonthValid = months.some(m => `${m.year}-${m.month}` === prevMonthKey);
    
    if (isPrevMonthValid) {
      setCurrentMonth(prevMonth);
      console.log("⬅️ Navigated to previous month:", prevMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
    }
  };

  const goToNextMonth = () => {
    if (!subscription || !currentMonth) return;
    
    const endDate = new Date(subscription.projectedEndDate);
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    
    // Check if next month is within subscription period
    const startDate = new Date(subscription.serviceStartDate || subscription.startDate);
    const months = getMonthsBetween(startDate, endDate);
    const nextMonthKey = `${nextMonth.getFullYear()}-${nextMonth.getMonth()}`;
    const isNextMonthValid = months.some(m => `${m.year}-${m.month}` === nextMonthKey);
    
    if (isNextMonthValid) {
      setCurrentMonth(nextMonth);
      console.log("➡️ Navigated to next month:", nextMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
    }
  };

  const canGoPrevious = () => {
    if (!subscription || !currentMonth) return false;
    
    const startDate = new Date(subscription.serviceStartDate || subscription.startDate);
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    
    // Check if previous month is within subscription period
    const months = getMonthsBetween(startDate, new Date(subscription.projectedEndDate));
    const prevMonthKey = `${prevMonth.getFullYear()}-${prevMonth.getMonth()}`;
    return months.some(m => `${m.year}-${m.month}` === prevMonthKey);
  };

  const canGoNext = () => {
    if (!subscription || !currentMonth) return false;
    
    const endDate = new Date(subscription.projectedEndDate);
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    
    // Check if next month is within subscription period
    const startDate = new Date(subscription.serviceStartDate || subscription.startDate);
    const months = getMonthsBetween(startDate, endDate);
    const nextMonthKey = `${nextMonth.getFullYear()}-${nextMonth.getMonth()}`;
    return months.some(m => `${m.year}-${m.month}` === nextMonthKey);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Loading customer calendar...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <p className="text-red-700 font-medium">Customer not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50">
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-green-700">JR Home Made Foods</h1>
              <p className="text-gray-600">Admin Panel</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/admin/customers')}
            className="text-green-600 hover:text-green-800 mr-2"
          >
            ← Back to Customers
          </button>
          <span className="text-gray-400 mx-2">/</span>
          <span className="text-gray-600">{customer.name || "Customer"}</span>
          <span className="text-gray-400 mx-2">/</span>
          <span className="text-gray-600">Calendar</span>
        </div>

        {/* Customer Info */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-green-700 mb-2">
                📅 Delivery Calendar - {customer.name || "Customer"}
              </h1>
              <div className="text-gray-600">
                <p><strong>Email:</strong> {customer.email || "—"}</p>
                <p><strong>Phone:</strong> {customer.phone || "—"}</p>
                <p><strong>Address:</strong> {customer.address || "—"}</p>
              </div>
            </div>
            <div className="text-right">
              {subscription ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-700 mb-2">Active Subscription</h3>
                  <div className="space-y-1 text-sm">
                    <div><strong>Plan:</strong> {subscription?.planName || "Basic Plan"}</div>
                    <div><strong>Rotis/Day:</strong> {subscription?.rotisPerDay || 2}</div>
                    <div><strong>Start Date:</strong> {formatDate(subscription?.serviceStartDate || subscription?.startDate)}</div>
                    <div><strong>Original End:</strong> {formatDate(subscription?.originalEndDate)}</div>
                    {subscription?.missedDays > 0 && (
                      <div><strong>Extended End:</strong> {formatDate(subscription?.projectedEndDate)} <span className="text-orange-600">(+{subscription.missedDays} missed days)</span></div>
                    )}
                    <div><strong>Status:</strong> <span className="text-green-600 font-semibold">{subscription?.status || "Active"}</span></div>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <p className="text-yellow-700 font-medium">No active subscription</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={goToPreviousMonth}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                canGoPrevious() 
                  ? 'bg-blue-500 text-white hover:bg-blue-600' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!canGoPrevious()}
            >
              ← Previous
            </button>
            
            <h2 className="text-xl font-bold text-gray-800">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Deliveries
            </h2>
            
            <button
              onClick={goToNextMonth}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                canGoNext() 
                  ? 'bg-blue-500 text-white hover:bg-blue-600' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!canGoNext()}
            >
              Next →
            </button>
          </div>
            
            {/* Holiday Notice */}
            <div className="bg-red-100 border-2 border-red-300 rounded-lg p-3 mb-4">
              <div className="text-red-700 font-semibold text-center">
                📢 Note: Every Monday will be Holiday (No deliveries)
              </div>
            </div>

            {/* Admin Instructions */}
            <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-3 mb-4">
              <div className="text-blue-700 font-semibold text-center">
                👆 Click on any delivery day to change its status (except Mondays which are holidays)
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2 text-sm" key={`calendar-${currentMonth?.getFullYear()}-${currentMonth?.getMonth()}`}>
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-semibold text-gray-600 p-2">
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {calendarDays.map(({ date, status, isInSubscriptionPeriod }, index) => (
                <div 
                  key={date || `empty-${index}`} 
                  className={`bg-white shadow-md rounded-xl p-2 text-center border border-gray-200 transition-all ${
                    date === null 
                      ? 'bg-gray-50 border-gray-100 cursor-default' 
                      : status === "monday_holiday" 
                        ? 'bg-red-50 border-red-200 cursor-not-allowed' 
                        : 'hover:shadow-lg hover:border-blue-300 cursor-pointer'
                  }`}
                  onClick={() => date && !status.startsWith("monday_holiday") && setEditingDate(date.toISOString().split('T')[0])}
                >
                  <div className="font-semibold text-gray-700 mb-1">
                    {date ? date.getDate() : ""}
                  </div>
                  <div className={`text-lg ${getStatusColor(status)}`}>
                    {getStatusEmoji(status)}
                  </div>
                  {status === "monday_holiday" && (
                    <div className="text-xs text-red-600 mt-1">Holiday</div>
                  )}
                  {date && !status.startsWith("monday_holiday") && editingDate === date.toISOString().split('T')[0] && (
                    <div className="absolute z-10 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-2">
                      <div className="text-xs font-semibold mb-2">Change Status:</div>
                      <div className="space-y-1">
                        {["delivered", "pending", "not_delivered", "upcoming"].map((statusOption) => (
                          <button
                            key={statusOption}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(date.toISOString().split('T')[0], statusOption);
                            }}
                            disabled={saving}
                            className={`block w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-100 ${
                              status === statusOption ? 'bg-blue-100 font-semibold' : ''
                            }`}
                          >
                            {getStatusEmoji(statusOption)} {statusOption.replace('_', ' ')}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-sm mt-4">
            <p><span className="text-green-600">✅</span> Delivered</p>
            <p><span className="text-red-600">❌</span> Not Delivered</p>
            <p><span className="text-blue-600">📅</span> Upcoming</p>
            <p><span className="text-yellow-600">⏳</span> Pending</p>
            <p><span className="text-gray-500 font-bold">NA</span> Service not available (Monday Holiday)</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-center space-x-4">
          <button
            onClick={() => navigate(`/admin/customers/${customerId}`)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-all"
          >
            👤 View Customer Profile
          </button>
          <button
            onClick={() => navigate('/admin/customers')}
            className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-all"
          >
            ← Back to Customers
          </button>
        </div>
        </div>


  );
};

export default AdminCustomerCalendar;

